import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Navbar } from "@/components/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { Package, Calendar, Clock, MapPin, CheckCircle, XCircle, AlertCircle, Trash2, History, RefreshCw, Plus, Camera, ZoomIn, CalendarPlus, Ban } from "lucide-react";
import { toast } from "sonner";
import type { PackageWithSchedule, Profile } from "@/types/database";
import { StorageImage } from "@/components/StorageImage";
import { ProofPhotoSection } from "@/components/tracking/ProofPhotoSection";
import { formatRetailerName } from "@/lib/formatRetailer";
import { notifyPickupAutoCanceled } from "@/lib/notifications";
import { CollapsibleSection } from "@/components/tracking/CollapsibleSection";
import { PickupStatusTimeline } from "@/components/tracking/PickupStatusTimeline";
import { PickupCard } from "@/components/tracking/PickupCard";
import { PrimaryActionCTA, type CTAState } from "@/components/tracking/PrimaryActionCTA";
import { PickupRequestsSection } from "@/components/dashboard/PickupRequestsSection";
import { ScheduledPickupBanner } from "@/components/dashboard/ScheduledPickupBanner";
import { SchedulePickupDialog } from "@/components/dashboard/SchedulePickupDialog";
import { AdminResponseSection } from "@/components/dashboard/AdminResponseSection";
import { CancelPackageDialog } from "@/components/dashboard/CancelPackageDialog";
import { NotificationsPanel } from "@/components/notifications/NotificationsPanel";
import { ResubmitPackageDialog } from "@/components/tracking/ResubmitPackageDialog";
import { usePickupLimits } from "@/hooks/usePickupLimits";
import { useNotifications } from "@/hooks/useNotifications";
import {
  isActivePackage,
  isHistoryPackage,
  canCancelPackage as canCancelPackageStatus,
  ACTIVE_PICKUP_STATUSES,
  PICKUP_STATUS,
} from "@/lib/pickupStatus";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Tracking = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activePackages, setActivePackages] = useState<PackageWithSchedule[]>([]);
  const [historyPackages, setHistoryPackages] = useState<PackageWithSchedule[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  
  // Active pickup state (single source of truth for current billing cycle)
  const [activePickup, setActivePickup] = useState<any>(null);
  const [pickupPackages, setPickupPackages] = useState<PackageWithSchedule[]>([]);
  
  // Image preview state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ bucket: string; path: string; title: string } | null>(null);
  
  // Schedule pickup dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [hasPendingPickupRequest, setHasPendingPickupRequest] = useState(false);
  
  // Pickup stage for timeline
  const [pickupStage, setPickupStage] = useState<"submitted" | "approved" | "scheduled" | "picked_up" | "returned" | null>(null);
  
  // Pickup status for CTA
  const [pickupStatus, setPickupStatus] = useState<"confirmed" | "awaiting_admin_confirmation" | "awaiting_confirmation" | "counter_proposed" | null>(null);

  // Cancel package dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingPackage, setCancellingPackage] = useState<PackageWithSchedule | null>(null);
const [cancelLoading, setCancelLoading] = useState(false);
  const [resubmittingPackageId, setResubmittingPackageId] = useState<string | null>(null);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [resubmitPackage, setResubmitPackage] = useState<PackageWithSchedule | null>(null);

  // Subscription info for pause checks
  const [subscriptionInfo, setSubscriptionInfo] = useState<{ 
    subscribed: boolean; 
    paused?: boolean; 
    expired?: boolean;
    expiredAt?: string;
  } | null>(null);

  // Subscription-based pickup limits
  const pickupLimits = usePickupLimits(user?.id);
  
  // In-app notifications
  const { 
    notifications, 
    dismiss: dismissNotification, 
    dismissAll: dismissAllNotifications,
    markAsRead 
  } = useNotifications(user?.id);

  const handleImagePreview = (bucket: string, path: string, title: string) => {
    setPreviewImage({ bucket, path, title });
    setImagePreviewOpen(true);
  };

  useEffect(() => {
    if (user) {
      fetchPackages();
      fetchProfile();
      checkPendingPickupRequests();
      fetchPickupStage();
      fetchActivePickup();
      checkSubscription();
    } else if (!loading) {
      setLoadingPackages(false);
    }
  }, [user, loading]);

  // Fetch the active pickup and its linked packages
  const fetchActivePickup = async () => {
    if (!user?.id) return;
    
    try {
      // Get active pickup
      const { data: pickupData, error: pickupError } = await supabase
        .from("user_pickups")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ACTIVE_PICKUP_STATUSES as unknown as string[])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (pickupError) throw pickupError;

      setActivePickup(pickupData);

      // If we have an active pickup, fetch packages linked to it
      if (pickupData) {
        const { data: linkedPackages, error: pkgError } = await (supabase as any)
          .from("packages")
          .select("*, pickup_schedules (*)")
          .eq("pickup_id", pickupData.id);

        if (pkgError) throw pkgError;
        setPickupPackages(linkedPackages || []);
      } else {
        setPickupPackages([]);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching active pickup:", error);
      }
    }
  };

  const checkSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscriptionInfo(data);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error checking subscription:", error);
      }
    }
  };

  const checkPendingPickupRequests = async () => {
    try {
      const { data, error } = await supabase
        .from("user_pickups")
        .select("id, status")
        .eq("user_id", user!.id)
        .in("status", ["awaiting_confirmation", "awaiting_admin_confirmation", "confirmed", "counter_proposed"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setHasPendingPickupRequest(!!data);
      setPickupStatus(data?.status as typeof pickupStatus || null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error checking pickup requests:", error);
      }
    }
  };

  // Derive the current pickup stage for the timeline
  const fetchPickupStage = async () => {
    try {
      // Check for any confirmed or scheduled pickup - include completed_at for 24h reset logic
      const { data: pickupData } = await supabase
        .from("user_pickups")
        .select("status, completed_at")
        .eq("user_id", user!.id)
        .in("status", ["confirmed", "awaiting_admin_confirmation", "awaiting_confirmation", "completed"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Check package statuses - include returned_at for 24h reset logic
      const { data: packageData } = await (supabase as any)
        .from("packages")
        .select("status, approval_status, returned_at")
        .eq("user_id", user!.id);

      const packages = packageData || [];
      
      // Check if 24 hours have passed since the most recent return/completion
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Check if completed pickup is within 24 hours
      const completedPickupRecent = pickupData?.status === "completed" && 
        pickupData.completed_at && 
        new Date(pickupData.completed_at) > twentyFourHoursAgo;
      
      // Check if any returned packages are within 24 hours
      const recentlyReturnedPackages = packages.filter((p: any) => 
        (p.status === "returned" || p.status === "delivered") && 
        p.returned_at && 
        new Date(p.returned_at) > twentyFourHoursAgo
      );
      const hasRecentReturn = recentlyReturnedPackages.length > 0;
      
      // Determine stage based on pickup and package statuses
      // Only consider truly active packages (not history items)
      const historyStatuses = ["returned", "delivered", "picked_up", "user_canceled", "archived", "resubmitted"];
      const activePackages = packages.filter((p: any) => 
        !historyStatuses.includes(p.status) && p.approval_status !== "rejected"
      );
      
      const hasCompletedPickup = pickupData?.status === "completed";
      const hasReturned = packages.some((p: any) => p.status === "returned" || p.status === "delivered");
      const hasPickedUp = packages.some((p: any) => p.status === "picked_up");
      const hasConfirmedPickup = pickupData?.status === "confirmed" || pickupData?.status === "awaiting_admin_confirmation" || pickupData?.status === "awaiting_confirmation";
      const hasApproved = activePackages.some((p: any) => p.approval_status === "approved");
      const hasPending = activePackages.some((p: any) => p.approval_status === "pending");

      // If returned/completed more than 24 hours ago, reset the timeline
      if ((hasCompletedPickup || hasReturned) && !completedPickupRecent && !hasRecentReturn) {
        // Check if there are any new active packages to show
        if (hasApproved) {
          setPickupStage("approved");
        } else if (hasPending) {
          setPickupStage("submitted");
        } else {
          setPickupStage(null); // Reset timeline completely
        }
      } else if (hasCompletedPickup || hasReturned) {
        setPickupStage("returned");
      } else if (hasPickedUp) {
        setPickupStage("picked_up");
      } else if (hasConfirmedPickup) {
        setPickupStage("scheduled");
      } else if (hasApproved) {
        setPickupStage("approved");
      } else if (hasPending) {
        setPickupStage("submitted");
      } else {
        setPickupStage(null); // No active packages, hide timeline
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching pickup stage:", error);
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("*")
        .eq("id", user!.id)
        .maybeSingle();

      if (error) throw error;
      setProfile(data as Profile | null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching profile:", error);
      }
    }
  };

  const fetchPackages = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("packages")
        .select(`
          *,
          pickup_schedules (*),
          user_pickups (pickup_confirmation_photo_url, completed_at, status)
        `)
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      const allPackages = (data || []) as PackageWithSchedule[];
      
      // Separate into active and history based on strict status rules
      const active: PackageWithSchedule[] = [];
      const history: PackageWithSchedule[] = [];
      
      // Calculate cutoffs for history visibility
      const now = new Date();
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      
      allPackages.forEach((pkg) => {
        const status = pkg.status;
        const approvalStatus = pkg.approval_status;
        
        // Use centralized status helpers
        if (isHistoryPackage(status, approvalStatus)) {
          // Rejected packages only show for 14 days
          if (approvalStatus === "rejected") {
            const packageDate = pkg.updated_at || pkg.created_at;
            const pkgDate = new Date(packageDate);
            if (pkgDate >= fourteenDaysAgo) {
              history.push(pkg);
            }
          } else {
            // Other history packages show for 90 days
            const packageDate = pkg.returned_at || pkg.canceled_at || pkg.archived_at || pkg.updated_at;
            const pkgDate = new Date(packageDate);
            if (pkgDate >= ninetyDaysAgo) {
              history.push(pkg);
            }
          }
        } else if (isActivePackage(status, approvalStatus)) {
          // Only add to active if both status and approval_status qualify
          active.push(pkg);
        } else {
          // Default to history for any edge cases - also apply 90-day filter
          const packageDate = pkg.returned_at || pkg.canceled_at || pkg.archived_at || pkg.updated_at;
          const pkgDate = new Date(packageDate);
          if (pkgDate >= ninetyDaysAgo) {
            history.push(pkg);
          }
        }
      });
      
      setActivePackages(active);
      setHistoryPackages(history);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching packages:", error);
      }
    } finally {
      setLoadingPackages(false);
    }
  };

  // Count approved packages ready for pickup
  const approvedPackageCount = activePackages.filter(
    (pkg) => pkg.approval_status === "approved"
  ).length;

  // Build user address from profile
  const userAddress = profile
    ? [profile.street, profile.city, profile.state, profile.zip]
        .filter(Boolean)
        .join(", ")
    : "";

  const handleScheduleSuccess = () => {
    checkPendingPickupRequests();
    pickupLimits.refetch();
  };

  // Determine if user can schedule based on approved packages, pending requests, and subscription limits
  const canSchedulePickup = 
    approvedPackageCount > 0 && 
    !hasPendingPickupRequest && 
    profile && 
    pickupLimits.canSchedule;

  const deletePackage = async (packageId: string) => {
    try {
      // First delete pickup schedules
      const { error: scheduleError } = await supabase
        .from("pickup_schedules")
        .delete()
        .eq("package_id", packageId);

      if (scheduleError) throw scheduleError;

      // Then delete the package
      const { error: packageError } = await supabase
        .from("packages")
        .delete()
        .eq("id", packageId);

      if (packageError) throw packageError;

      toast.success("Package deleted successfully");
      fetchPackages();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error deleting package:", error);
      }
      toast.error("Failed to delete package");
    }
  };

  // Check if a package can be cancelled based on its status - using centralized logic
  const canCancelPackage = (pkg: PackageWithSchedule) => {
    const status = pkg.status?.toLowerCase() || "";
    const approvalStatus = pkg.approval_status?.toLowerCase() || "";
    
    return canCancelPackageStatus(status, approvalStatus);
  };

  const handleCancelClick = (pkg: PackageWithSchedule) => {
    setCancellingPackage(pkg);
    setCancelDialogOpen(true);
  };

  const cancelPackage = async (reason: string) => {
    if (!cancellingPackage) return;
    
    setCancelLoading(true);
    try {
      const pickupIdToCheck = cancellingPackage.pickup_id;

      // First delete any pickup schedules for this package
      const { error: scheduleError } = await supabase
        .from("pickup_schedules")
        .delete()
        .eq("package_id", cancellingPackage.id);

      if (scheduleError) throw scheduleError;

      // Update the package: status to user_canceled, clear pickup_id, store reason
      const { error: packageError } = await supabase
        .from("packages")
        .update({
          status: "user_canceled",
          canceled_at: new Date().toISOString(),
          cancel_reason: reason || null,
          pickup_id: null, // Remove from pickup association
        })
        .eq("id", cancellingPackage.id);

      if (packageError) throw packageError;

      // Check if pickup has remaining packages and auto-cancel if none remain
      let pickupAutoCanceled = false;
      if (pickupIdToCheck) {
        // Count remaining packages still linked to this pickup
        const { count, error: countError } = await supabase
          .from("packages")
          .select("id", { count: "exact", head: true })
          .eq("pickup_id", pickupIdToCheck)
          .not("status", "eq", "user_canceled");

        if (!countError && count === 0) {
          // No packages remain - auto-cancel the pickup
          const { error: pickupError } = await supabase
            .from("user_pickups")
            .update({
              status: "auto_canceled",
              cancelled_at: new Date().toISOString(),
              cancelled_reason: "No packages remain",
            })
            .eq("id", pickupIdToCheck);

          if (!pickupError) {
            pickupAutoCanceled = true;
            // Create notification for auto-cancel (user can insert their own notifications)
            await notifyPickupAutoCanceled(user!.id);
          }
        }
      }

      if (pickupAutoCanceled) {
        toast.success("Package canceled. Your pickup was also canceled because no packages remain.", {
          duration: 5000,
        });
      } else {
        toast.success("Package canceled successfully");
      }

      setCancelDialogOpen(false);
      setCancellingPackage(null);
      fetchPackages();
      checkPendingPickupRequests();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error canceling package:", error);
      }
      toast.error("Failed to cancel package");
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle resubmitting a rejected package (one-time only)
  const handleResubmitPackage = async (pkg: PackageWithSchedule) => {
    if ((pkg as any).was_resubmitted) {
      toast.error("This package has already been resubmitted");
      return;
    }
    
    setResubmittingPackageId(pkg.id);
    try {
      // Create a new package as resubmission
      const { data: newPackage, error: insertError } = await supabase
        .from("packages")
        .insert({
          user_id: user!.id,
          retailer: pkg.retailer,
          package_description: pkg.package_description,
          tracking_number: pkg.tracking_number,
          size: pkg.size,
          city: pkg.city,
          notes: pkg.notes,
          print_qr_needed: pkg.print_qr_needed,
          qr_code_url: pkg.qr_code_url, // Copy QR code URL to satisfy constraint
          package_photo_url: pkg.package_photo_url, // Copy package photo as well
          location_code: pkg.location_code,
          return_by_date: pkg.return_by_date,
          status: "pending",
          approval_status: "pending",
          resubmitted_from_package_id: pkg.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Mark original package as resubmitted (one-time only)
      const { error: updateError } = await supabase
        .from("packages")
        .update({
          was_resubmitted: true,
          status: "resubmitted",
        })
        .eq("id", pkg.id);

      if (updateError) throw updateError;

      toast.success("Package resubmitted successfully");
      fetchPackages();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error resubmitting package:", error);
      }
      toast.error("Failed to resubmit package");
    } finally {
      setResubmittingPackageId(null);
    }
  };

  // Check if a rejected package can be resubmitted (only once)
  const canResubmitPackage = (pkg: PackageWithSchedule): boolean => {
    return (
      pkg.approval_status === "rejected" &&
      pkg.status !== "resubmitted" &&
      !(pkg as any).was_resubmitted
    );
  };

  if (loading || loadingPackages) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const getApprovalBadge = (approvalStatus: string) => {
    switch (approvalStatus) {
      case "pending":
        return (
          <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <AlertCircle className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "approved":
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge>{approvalStatus}</Badge>;
    }
  };

  const getStatusBadge = (pkg: PackageWithSchedule) => {
    const status = pkg.status;
    const approvalStatus = pkg.approval_status;
    
    if (approvalStatus === "rejected") {
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </Badge>
      );
    }
    
    switch (status) {
      case "delivered":
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Delivered
          </Badge>
        );
      case "picked_up":
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Picked Up
          </Badge>
        );
      case "returned":
        return (
          <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/20">
            <CheckCircle className="w-3 h-3 mr-1" />
            Returned
          </Badge>
        );
      case "archived":
        return (
          <Badge className="bg-muted text-muted-foreground">
            Archived
          </Badge>
        );
      case "resubmitted":
        return (
          <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20">
            <RefreshCw className="w-3 h-3 mr-1" />
            Resubmitted
          </Badge>
        );
      case "user_canceled":
        return (
          <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/20">
            <Ban className="w-3 h-3 mr-1" />
            Canceled
          </Badge>
        );
      default:
        return (
          <Badge className="bg-muted text-muted-foreground">
            {status}
          </Badge>
        );
    }
  };

  const renderActivePackageCard = (pkg: PackageWithSchedule) => (
    <Card key={pkg.id} className="shadow-soft hover:shadow-medium transition-shadow">
      <CardHeader className="px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Package className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
              <span className="truncate">{formatRetailerName(pkg.retailer)}</span>
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 self-start">
            {getApprovalBadge(pkg.approval_status || "pending")}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Package & QR Photos */}
        {(pkg.package_photo_url || pkg.qr_code_url) && (
          <div className="flex items-center gap-3 mb-4">
            {pkg.package_photo_url && (
              <div 
                className="w-14 h-14 rounded-lg bg-muted overflow-hidden border cursor-pointer group relative flex-shrink-0"
                onClick={() => handleImagePreview("package-photos", pkg.package_photo_url!, "Package Photo")}
              >
                <StorageImage bucket="package-photos" storedValue={pkg.package_photo_url} alt="Package" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
            {pkg.qr_code_url && (
              <div 
                className="w-14 h-14 rounded-lg bg-muted overflow-hidden border cursor-pointer group relative flex-shrink-0"
                onClick={() => handleImagePreview("qr-codes", pkg.qr_code_url!, "QR Code")}
              >
                <StorageImage bucket="qr-codes" storedValue={pkg.qr_code_url} alt="QR Code" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
          </div>
        )}

        {pkg.package_description && (
          <p className="text-sm text-muted-foreground mb-4">
            {pkg.package_description}
          </p>
        )}
        
        {/* Cancel Package Button */}
        {canCancelPackage(pkg) && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 min-h-[44px] w-full sm:w-auto"
            onClick={() => handleCancelClick(pkg)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Cancel Package
          </Button>
        )}
      </CardContent>
    </Card>
  );

  const renderHistoryPackageCard = (pkg: PackageWithSchedule) => (
    <Card key={pkg.id} className="shadow-soft opacity-80">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-muted-foreground" />
              {formatRetailerName(pkg.retailer)}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(pkg)}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Package & QR Photos */}
        {(pkg.package_photo_url || pkg.qr_code_url) && (
          <div className="flex items-center gap-3">
            {pkg.package_photo_url && (
              <div 
                className="w-14 h-14 rounded-lg bg-muted overflow-hidden border cursor-pointer group relative flex-shrink-0"
                onClick={() => handleImagePreview("package-photos", pkg.package_photo_url!, "Package Photo")}
              >
                <StorageImage bucket="package-photos" storedValue={pkg.package_photo_url} alt="Package" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
            {pkg.qr_code_url && (
              <div 
                className="w-14 h-14 rounded-lg bg-muted overflow-hidden border cursor-pointer group relative flex-shrink-0"
                onClick={() => handleImagePreview("qr-codes", pkg.qr_code_url!, "QR Code")}
              >
                <StorageImage bucket="qr-codes" storedValue={pkg.qr_code_url} alt="QR Code" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </div>
            )}
          </div>
        )}
        {/* Rejection reason display with resubmit button */}
        {pkg.approval_status === "rejected" && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg space-y-3">
            {pkg.rejection_reason && (
              <>
                <p className="text-sm font-medium text-red-600">Rejection Reason:</p>
                <p className="text-sm text-red-500">{pkg.rejection_reason}</p>
              </>
            )}
            
            {/* Resubmit button - only if not already resubmitted */}
            {canResubmitPackage(pkg) && (
              <Button
                variant="outline"
                size="sm"
                className="w-full border-primary/50 text-primary hover:bg-primary/10"
                onClick={() => {
                  setResubmitPackage(pkg);
                  setResubmitDialogOpen(true);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Edit & Resubmit Package
              </Button>
            )}
            
            {/* Already resubmitted indicator */}
            {(pkg as any).was_resubmitted && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />
                Already resubmitted
              </p>
            )}
          </div>
        )}
        
        {/* Resubmission info - if this is a resubmitted package */}
        {pkg.status === "resubmitted" && (
          <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <p className="text-sm font-medium text-orange-600 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              Resubmission
            </p>
            <p className="text-xs text-orange-500 mt-1">
              Submitted on {new Date(pkg.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        )}
        
        {/* Proof of Pickup Section - per-package or per-pickup confirmation photo */}
        {(pkg.status === "picked_up" || pkg.status === "returned") && (
          <ProofPhotoSection
            bucket="pickup-proof"
            storedValue={pkg.proof_of_pickup_url || (pkg as any).user_pickups?.pickup_confirmation_photo_url}
            label="Pickup Confirmation"
            colorClass="blue"
            onPreview={handleImagePreview}
          />
        )}
        
        {pkg.status === "delivered" && (
          <ProofPhotoSection
            bucket="proof-of-delivery"
            storedValue={pkg.proof_of_delivery_url}
            label="Proof of Delivery"
            colorClass="green"
            onPreview={handleImagePreview}
          />
        )}
        
        {/* Completion date for picked up/delivered packages */}
        {(pkg.status === "picked_up" || pkg.status === "delivered") && pkg.pickup_schedules && pkg.pickup_schedules.length > 0 && (
          <div className="text-sm text-muted-foreground">
            <span>{pkg.status === "delivered" ? "Delivered" : "Picked up"} on: </span>
            {new Date(pkg.pickup_schedules[0].pickup_date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        )}
        
        {/* Return receipt info */}
        {pkg.status === "returned" && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground space-y-1">
              {pkg.returned_at && (
                <p>Returned on: {new Date(pkg.returned_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}</p>
              )}
              {pkg.confirmation_code && (
                <p className="font-mono">Confirmation Code: <span className="font-medium text-foreground">{pkg.confirmation_code}</span></p>
              )}
            </div>
            <ProofPhotoSection
              bucket="return-receipts"
              storedValue={pkg.return_receipt_url}
              label="Return Receipt"
              colorClass="teal"
              onPreview={handleImagePreview}
            />
            <ProofPhotoSection
              bucket="proof-of-delivery"
              storedValue={pkg.proof_of_delivery_url}
              label="Proof of Delivery"
              colorClass="green"
              onPreview={handleImagePreview}
            />
          </div>
        )}

        {/* Canceled package info */}
        {pkg.status === "user_canceled" && (
          <div className="p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <Ban className="w-3 h-3" />
              Canceled by you
            </p>
            {pkg.canceled_at && (
              <p className="text-xs text-gray-500 mt-1">
                On {new Date(pkg.canceled_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
            {pkg.cancel_reason && (
              <p className="text-sm text-gray-500 mt-2">
                Reason: {pkg.cancel_reason}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Determine the primary CTA state based on user's current situation
  const getPrimaryCTAState = (): CTAState => {
    // Check if subscription is paused - disable new scheduling (but allow existing confirmed pickups)
    const isSubscriptionPaused = subscriptionInfo?.paused === true;
    const isSubscriptionExpired = subscriptionInfo?.expired === true;
    
    // If subscription has expired, prompt to resubscribe
    if (isSubscriptionExpired && !subscriptionInfo?.subscribed) {
      return "subscription_expired";
    }
    
    // No packages at all
    if (activePackages.length === 0 && historyPackages.length === 0) {
      // If paused, still show add_package but it will redirect to dashboard which shows paused banner
      return isSubscriptionPaused ? "subscription_paused" : "add_package";
    }
    
    // Has packages but all are pending approval
    const hasPendingPackages = activePackages.some(p => p.approval_status === "pending");
    const hasApprovedPackages = activePackages.some(p => p.approval_status === "approved");
    
    if (hasPendingPackages && !hasApprovedPackages && !hasPendingPickupRequest) {
      return "pending_packages";
    }
    
    // Has confirmed pickup - can edit (allow even if paused)
    if (pickupStatus === "confirmed") {
      return "edit_pickup";
    }
    
    // Awaiting admin approval for pickup (allow even if paused - already in progress)
    if (pickupStatus === "awaiting_admin_confirmation") {
      return "awaiting_approval";
    }
    
    // Awaiting user confirmation (counter-proposal from admin - allow even if paused)
    if (pickupStatus === "awaiting_confirmation" || pickupStatus === "counter_proposed") {
      return "view_scheduled";
    }
    
    // Has approved packages but no pickup scheduled
    if (hasApprovedPackages && !hasPendingPickupRequest) {
      // If paused, show paused state instead of schedule
      if (isSubscriptionPaused) {
        return "subscription_paused";
      }
      if (!pickupLimits.canSchedule) {
        return "limit_reached";
      }
      return "schedule_pickup";
    }
    
    // If paused and trying to add package
    if (isSubscriptionPaused) {
      return "subscription_paused";
    }
    // Default: add package
    return "add_package";
  };

  // Handle CTA action based on state
  const handlePrimaryCTAAction = () => {
    const state = getPrimaryCTAState();
    
    switch (state) {
      case "add_package":
        navigate("/dashboard");
        break;
      case "schedule_pickup":
        setScheduleDialogOpen(true);
        break;
      case "edit_pickup":
      case "view_scheduled":
        // Scroll to the pickup banner
        const banner = document.querySelector('[data-pickup-banner]');
        banner?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      case "awaiting_approval":
      case "limit_reached":
      case "pending_packages":
      case "subscription_paused":
        // Disabled states - no action (user should go to settings to resume)
        break;
      case "subscription_expired":
        // Navigate to settings to resubscribe
        navigate("/settings");
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Navbar />
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold mb-2">Track Your Returns</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                View and manage your scheduled returns
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              {/* Pickup limits info */}
              {profile && !pickupLimits.loading && activePackages.length > 0 && (
                <Badge variant={pickupLimits.canSchedule ? "secondary" : "outline"} className="whitespace-nowrap">
                  {pickupLimits.remainingPickups} of {pickupLimits.maxPickups} pickup{pickupLimits.maxPickups !== 1 ? 's' : ''} left
                </Badge>
              )}
              
              {/* Single context-aware CTA */}
              <PrimaryActionCTA
                state={getPrimaryCTAState()}
                onAction={handlePrimaryCTAAction}
              />
            </div>
          </div>

          {activePackages.length === 0 && historyPackages.length === 0 ? (
            <Card className="text-center py-16 border-dashed">
              <CardContent className="space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">No packages yet</h3>
                  <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
                    Ready to return something? Use the button above to add your first package.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* In-App Notifications */}
              {notifications.length > 0 && (
                <NotificationsPanel
                  notifications={notifications}
                  onDismiss={dismissNotification}
                  onDismissAll={dismissAllNotifications}
                  onMarkAsRead={markAsRead}
                />
              )}

              {/* Pickup Status Timeline */}
              {pickupStage && (
                <Card className="shadow-soft">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Pickup Status</CardTitle>
                    <CardDescription>Track the progress of your return</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <PickupStatusTimeline currentStage={pickupStage} />
                  </CardContent>
                </Card>
              )}

              {/* Current Pickup Card - Shows one card per billing cycle with packages inside */}
              <PickupCard
                pickup={activePickup}
                packages={activePickup ? pickupPackages : activePackages}
                onCancelPackage={handleCancelClick}
                canCancelPackage={canCancelPackage}
              />

              {/* Scheduled Pickup Banner - Shows confirmed pickup date/time for all packages */}
              {user && <ScheduledPickupBanner userId={user.id} onPickupUpdated={checkPendingPickupRequests} />}

              {/* Admin Response Section - Shows when admin denied user's request */}
              {user && <AdminResponseSection userId={user.id} onResponse={checkPendingPickupRequests} />}

              {/* Pickup Approval Requests - Shows when admin proposes pickup times */}
              {user && (
                <PickupRequestsSection
                  userId={user.id}
                  subscriptionType={profile?.subscription_type || null}
                />
              )}

              {/* Unscheduled Active Packages - Only show if there are packages NOT linked to the active pickup */}
              {activePackages.filter(p => !p.pickup_id || p.pickup_id !== activePickup?.id).length > 0 && (
                <CollapsibleSection
                  title="Packages Awaiting Pickup"
                  count={activePackages.filter(p => !p.pickup_id || p.pickup_id !== activePickup?.id).length}
                  icon={<Package className="w-5 h-5 text-primary" />}
                  defaultOpen={true}
                  maxVisible={5}
                  items={activePackages
                    .filter(p => !p.pickup_id || p.pickup_id !== activePickup?.id)
                    .map(renderActivePackageCard)}
                />
              )}

              {/* Package History - Collapsible, default collapsed, 90-day retention */}
              <CollapsibleSection
                title="Package History"
                count={historyPackages.length}
                icon={<History className="w-5 h-5" />}
                defaultOpen={false}
                muted={true}
                maxVisible={5}
                items={historyPackages.length > 0 ? historyPackages.map(renderHistoryPackageCard) : undefined}
              >
                {historyPackages.length === 0 && (
                  <Card className="text-center py-10 border-dashed">
                    <CardContent className="space-y-3">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <History className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">No package history yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Your completed and canceled packages will appear here for 90 days.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>
      </div>
      
      {/* Image Preview Dialog */}
      <Dialog open={imagePreviewOpen} onOpenChange={setImagePreviewOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{previewImage?.title || "Image Preview"}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {previewImage && (
              <StorageImage
                bucket={previewImage.bucket}
                storedValue={previewImage.path}
                alt={previewImage.title}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Pickup Dialog */}
      {user && profile && (
        <SchedulePickupDialog
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          userId={user.id}
          userAddress={userAddress}
          approvedPackageCount={approvedPackageCount}
          onSuccess={handleScheduleSuccess}
        />
      )}

      {/* Cancel Package Dialog */}
      <CancelPackageDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={cancelPackage}
        loading={cancelLoading}
        packageRetailer={cancellingPackage ? formatRetailerName(cancellingPackage.retailer) : undefined}
        isPartOfScheduledPickup={hasPendingPickupRequest && cancellingPackage?.approval_status === "approved"}
      />

      {/* Resubmit Package Dialog */}
      {user && (
        <ResubmitPackageDialog
          open={resubmitDialogOpen}
          onOpenChange={setResubmitDialogOpen}
          pkg={resubmitPackage}
          userId={user.id}
          onSuccess={fetchPackages}
        />
      )}
    </div>
  );
};

export default Tracking;
